import { useEffect, useMemo, useRef, useState } from 'react';
import { Board } from './Board';
import { createGameState } from '../game';
import { HintHighlight, HintRule, hintHighlight } from '../hints';
import { Technique, resolveExample } from '../techniques';
import { ruleColor } from '../../shared/analysis/rulemeta';

// The worked-example strip from Tips & Tricks, on its own so the technique modal on a player
// profile shows literally the same pictures rather than a second, drifting copy of them.
//
// Every board here is rendered by the game's own `Board` with the game's own hint highlight, from
// a deduction the real engine found — see techniques.ts.
//
// Two things the pictures do that a static diagram can't. They *cycle*: the position as you would
// meet it, then the squares the deduction reasons about, then its verdict in red — because a
// board showing tint, accents and ✕s all at once is unreadable, and the interesting part is which
// of those the reader has to supply themselves. And there are three of each, on a scroll-snap
// strip, because one board teaches a board and three teach a shape.

/**
 * R1 is a view concern rather than a rule instance, so it has no entry in the analyzer's color
 * table (which only names the rules the deduction graph is built from). Give it a neutral gray
 * here rather than letting it fall through to the "unknown rule" color.
 */
const R1_COLOR = '#94a3b8';
export const dotColor = (rule: HintRule) => (rule === 'R1' ? R1_COLOR : ruleColor(rule));

/** How long each stage of the cycle holds. The verdict lingers; it is the one worth reading. */
const PHASE_MS = [1300, 1600, 2600];

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * The three worked examples, on a scroll-snap strip so a swipe browses them on a phone and the
 * dots do the same on a desktop. Only the visible section animates, and the cycle restarts when
 * the reader moves to another example so they always see it from the beginning.
 */
export function ExampleCarousel({ technique }: { technique: Technique }) {
  const t = technique;
  const [index, setIndex] = useState(0);
  const stripRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const onScreen = useOnScreen(sectionRef);
  const phase = usePhaseCycle(onScreen, index);

  // Resolving runs the rule engine; nothing about it changes, so do it once per example.
  const resolved = useMemo(() => t.examples.map(ex => {
    try {
      return { ex, ...resolveExample(t, ex) };
    } catch (e) {
      console.error('[queens] technique example failed:', t.rule, e);
      return null;
    }
  }), [t]);

  // Where a button press is steering to, until the strip gets there. An animated scrollTo emits
  // scroll events all the way across, and the first half of them still round to the slide we are
  // leaving — without this the caption and the dots snap back before landing.
  const steeringTo = useRef<number | null>(null);
  const giveUp = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (giveUp.current) clearTimeout(giveUp.current); }, []);

  const goTo = (i: number) => {
    const strip = stripRef.current;
    const next = Math.max(0, Math.min(resolved.length - 1, i));
    setIndex(next);
    if (!strip) return;
    steeringTo.current = next;
    // Whatever happens — an interrupted scroll, a browser that never lands exactly — hand
    // control back to the strip shortly after the animation would have finished.
    if (giveUp.current) clearTimeout(giveUp.current);
    giveUp.current = setTimeout(() => { steeringTo.current = null; }, 800);
    strip.scrollTo({ left: next * strip.clientWidth, behavior: 'smooth' });
  };

  // The strip is the source of truth once a finger is on it, so read the index back off it.
  const onScroll = () => {
    const strip = stripRef.current;
    if (!strip || strip.clientWidth === 0) return;
    const at = Math.round(strip.scrollLeft / strip.clientWidth);
    if (steeringTo.current !== null) {
      if (at === steeringTo.current) steeringTo.current = null;
      return;
    }
    if (at !== index) setIndex(at);
  };

  return (
    <div className="tech-example" ref={sectionRef}>
      {/* Boards only: equal-height slides make the snap land square, and it keeps the dots
          directly under the picture instead of below a paragraph of caption. */}
      <div className="tech-strip" ref={stripRef} onScroll={onScroll}>
        {resolved.map((r, i) => (
          <div className="tech-slide" key={i}>
            {r
              // Off-slide boards sit at stage 0. Parking them on the finished picture instead
              // would flash the answer for the frames a swipe takes to cross the midpoint,
              // before the cycle restarts and wipes it.
              ? <ExampleBoard {...r} phase={i === index ? phase : 0} />
              : <p className="tech-dim">No example available.</p>}
          </div>
        ))}
      </div>

      <div className="tech-pager">
        <button className="tech-arrow" onClick={() => goTo(index - 1)} disabled={index === 0} aria-label="Previous example">‹</button>
        <div className="tech-dots">
          {resolved.map((_, i) => (
            <button
              key={i}
              className={i === index ? 'tech-pip tech-pip-on' : 'tech-pip'}
              onClick={() => goTo(i)}
              aria-label={`Example ${i + 1} of ${resolved.length}`}
              aria-current={i === index}
            />
          ))}
        </div>
        <button className="tech-arrow" onClick={() => goTo(index + 1)} disabled={index === resolved.length - 1} aria-label="Next example">›</button>
      </div>

      {resolved.map((r, i) => r && (
        <div key={i} className={i === index ? 'tech-cap' : 'tech-cap tech-cap-off'} aria-hidden={i !== index}>
          <p className="tech-caption">{r.ex.caption}</p>
          <p className="tech-quote">The hints panel puts it: “{r.hint.explanation}”</p>
        </div>
      ))}
    </div>
  );
}

/**
 * One board at one stage of the cycle: 0 the bare position, 1 the squares the deduction reasons
 * about, 2 the verdict. Stage 1 is the full highlight with the outcome taken back out, so the
 * reader gets a moment to look at the same thing the rule looks at before being told the answer.
 */
function ExampleBoard({ puzzle, board, hint, phase }: ReturnType<typeof resolveExample> & { phase: number }) {
  const state = useMemo(() => ({ ...createGameState(puzzle), board }), [puzzle, board]);
  const full = useMemo(() => hintHighlight(hint), [hint]);
  const highlight: HintHighlight | null =
    phase === 0 ? null
      : phase === 1 ? { ...full, elim: new Set<string>(), place: new Set<string>() }
        : full;

  return (
    <div className="tech-board">
      <Board puzzle={puzzle} state={state} onStateChange={() => {}} readOnly highlight={highlight} />
    </div>
  );
}

/** True while the element is on screen — nothing off screen has any business animating. */
function useOnScreen(ref: React.RefObject<HTMLElement | null>) {
  // With no observer to ask, assume visible: a still board beats a permanently blank one.
  const [on, setOn] = useState(typeof IntersectionObserver === 'undefined');
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(([entry]) => setOn(entry.isIntersecting));
    io.observe(el);
    return () => io.disconnect();
  }, [ref]);
  return on;
}

/**
 * Walk 0 → 1 → 2 → 0 while enabled. Off screen it waits at stage 0 rather than on the finished
 * picture, so scrolling a section into view doesn't flash the answer before the cycle restarts.
 */
function usePhaseCycle(enabled: boolean, restartKey: unknown) {
  const [phase, setPhase] = useState(2);
  useEffect(() => {
    if (prefersReducedMotion()) { setPhase(2); return; }
    if (!enabled) { setPhase(0); return; }
    let timer: ReturnType<typeof setTimeout>;
    let at = 0;
    setPhase(0);
    const step = () => {
      timer = setTimeout(() => {
        at = (at + 1) % PHASE_MS.length;
        setPhase(at);
        step();
      }, PHASE_MS[at]);
    };
    step();
    return () => clearTimeout(timer);
  }, [enabled, restartKey]);
  return phase;
}
